import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import PassAccordion from './PassAccordion';
import {
  Loader2,
  Users,
  Trophy,
  Ticket,
  Lock,
  Clock,
  Info,
  CheckCircle,
  Plus,
  ChevronDown,
  ChevronUp,
  ArrowUpCircle,
  ExternalLink
} from 'lucide-react';
import { companyAPI } from '../../services/api';
import { toast } from '../../hooks/use-toast';

export default function NetworkingPassesModal({ open, onClose, onOpenFOModal }) {
  const [fetchingData, setFetchingData] = useState(false);
  const [passData, setPassData] = useState(null);
  const [activePassType, setActivePassType] = useState(null); // 'gold' or 'silver' or null
  const [expandedPassIndex, setExpandedPassIndex] = useState(null); // for viewing claimed passes

  // Polling state for ticket code auto-refresh
  const [pollingEmail, setPollingEmail] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const pollingTimeoutRef = useRef(null);

  // Fetch pass data when modal opens
  useEffect(() => {
    if (open) {
      fetchPassData();
    } else {
      // Cancel any ongoing polling when modal closes
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      setPollingEmail(null);
      setPollAttempts(0);
    }
  }, [open]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const fetchPassData = async () => {
    setFetchingData(true);
    try {
      const response = await companyAPI.getNetworkingPasses();
      if (response.data.success) {
        setPassData(response.data);
      }
    } catch (error) {
      console.error('Error fetching pass data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pass information',
        variant: 'destructive'
      });
    } finally {
      setFetchingData(false);
    }
  };

  const startPolling = (email, passType, attempt = 0) => {
    // Clear any existing timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }

    // Maximum 3 attempts
    if (attempt >= 3) {
      console.log('[Polling] Max attempts reached for', email);
      setPollingEmail(null);
      setPollAttempts(0);
      return;
    }

    // Calculate delay: 3s for attempts 0 & 1, 4s for attempt 2
    const delay = attempt < 2 ? 3000 : 4000;

    console.log(`[Polling] Scheduling poll #${attempt + 1} for ${email} in ${delay}ms`);

    pollingTimeoutRef.current = setTimeout(async () => {
      try {
        console.log(`[Polling] Executing poll #${attempt + 1} for ${email}`);

        // Fetch latest pass data
        const response = await companyAPI.getNetworkingPasses();

        if (response.data.success) {
          // Find the claimed pass by email
          const passArray = passType === 'silverPlus'
            ? response.data.silverPlusPasses
            : response.data.silverPasses;

          const claimedPass = passArray?.find(
            p => p.email.toLowerCase() === email.toLowerCase()
          );

          // Check if ticket code has arrived
          if (claimedPass?.ticketCode) {
            console.log('[Polling] Ticket code found:', claimedPass.ticketCode);

            // Update UI with new data
            setPassData(response.data);

            // Stop polling - success!
            setPollingEmail(null);
            setPollAttempts(0);

            // Optional: Show success toast
            // toast({
            //   title: 'Ticket code received',
            //   description: 'Your pass upgrade is now available',
            // });
          } else {
            console.log(`[Polling] No ticket code yet (attempt ${attempt + 1}/3)`);

            // Update UI anyway (in case other data changed)
            setPassData(response.data);

            // Schedule next retry
            setPollAttempts(attempt + 1);
            startPolling(email, passType, attempt + 1);
          }
        }
      } catch (error) {
        console.error('[Polling] Error fetching pass data:', error);

        // Continue polling despite error (network might recover)
        setPollAttempts(attempt + 1);
        startPolling(email, passType, attempt + 1);
      }
    }, delay);
  };

  const handleClaimClick = (passType) => {
    setActivePassType(passType);
    setExpandedPassIndex(null); // Close any viewing claimed passes
  };

  const handleCancelClaim = () => {
    setActivePassType(null);
  };

  const handleClaim = async (passType, attendeeData) => {
    try {
      const response = await companyAPI.claimNetworkingPass(passType, attendeeData);

      if (response.data.success) {
        const passLabel = passType === 'gold' ? 'Gold' : passType === 'silverPlus' ? 'Silver Plus' : 'Silver';
        toast({
          title: 'Success!',
          description: `${passLabel} pass claimed successfully`,
          variant: 'default'
        });

        // Clear active form
        setActivePassType(null);

        // Refresh data
        await fetchPassData();

        // Start polling for ticket code (only for silverPlus and silver)
        if (passType === 'silverPlus' || passType === 'silver') {
          setPollingEmail(attendeeData.email);
          setPollAttempts(0);
          startPolling(attendeeData.email, passType, 0);
        }
      }
    } catch (error) {
      console.error('Error claiming pass:', error);

      // Handle conflict errors
      if (error.response?.data?.conflicts) {
        toast({
          title: 'Duplicate Data Detected',
          description: 'Please check the form for conflict messages',
          variant: 'destructive'
        });
        throw error; // Re-throw to let PassAccordion handle it
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to claim pass',
          variant: 'destructive'
        });
        throw error;
      }
    }
  };

  const toggleClaimedPass = (passType, index) => {
    const key = `${passType}-${index}`;
    if (expandedPassIndex === key) {
      setExpandedPassIndex(null);
    } else {
      setExpandedPassIndex(key);
      setActivePassType(null); // Close any claim forms
    }
  };

  const renderPassSlots = (passType) => {
    let quota, claimed, passes, Icon, color;

    if (passType === 'gold') {
      quota = passData.goldQuota;
      claimed = passData.goldClaimed;
      passes = passData.goldPasses;
      Icon = Trophy;
      color = 'yellow';
    } else if (passType === 'silverPlus') {
      quota = passData.silverPlusQuota;
      claimed = passData.silverPlusClaimed;
      passes = passData.silverPlusPasses;
      Icon = Ticket;
      color = 'gray';
    } else {
      quota = passData.silverQuota;
      claimed = passData.silverClaimed;  // Only manual claims
      passes = passData.silverPasses;  // All silver passes (manual + FO)
      Icon = Ticket;
      color = 'gray';
    }

    const slots = [];

    // Special handling for Gold Passes - presenter reservation (only for role='company')
    let presenterPass = null;
    let nonPresenterPasses = passes;

    // Special handling for Silver Passes - separate manual claims from FO bonus
    let silverManualPasses = passes;
    let silverFOPasses = [];
    if (passType === 'silver') {
      silverManualPasses = passes?.filter(p => p.isFONominee !== true) || [];
      silverFOPasses = passes?.filter(p => p.isFONominee === true) || [];
      console.log('Silver passes debug:', {
        total: passes?.length,
        manual: silverManualPasses.length,
        fo: silverFOPasses.length,
        manualPasses: silverManualPasses,
        foPasses: silverFOPasses
      });
    }

    if (passType === 'gold' && passData.companyRole === 'company' && quota > 0) {
      // Find presenter pass
      presenterPass = passes?.find(p => p.isPresenter === true);
      // Separate non-presenter passes
      nonPresenterPasses = passes?.filter(p => p.isPresenter !== true) || [];

      // Always show presenter slot FIRST
      if (presenterPass) {
        // Show presenter pass
        const isExpanded = expandedPassIndex === 'gold-presenter';
        slots.push(
          <div key="gold-presenter" className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-between border-green-300 bg-green-50 hover:bg-green-100 hover:text-gray-900"
              onClick={() => {
                setExpandedPassIndex(isExpanded ? null : 'gold-presenter');
                setActivePassType(null);
              }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="font-medium">
                  {presenterPass.firstName} {presenterPass.lastName} - {presenterPass.designation}
                </span>
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full border border-yellow-300">
                  Presenter
                </span>
              </div>
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {isExpanded && (
              <Card className="border-green-200">
                <CardContent className="pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-muted-foreground">First Name</p><p className="font-medium">{presenterPass.firstName}</p></div>
                    <div><p className="text-muted-foreground">Last Name</p><p className="font-medium">{presenterPass.lastName}</p></div>
                    <div><p className="text-muted-foreground">Designation</p><p className="font-medium">{presenterPass.designation}</p></div>
                    <div><p className="text-muted-foreground">Company</p><p className="font-medium">{presenterPass.company}</p></div>
                    <div><p className="text-muted-foreground">Phone</p><p className="font-medium">{presenterPass.phone?.countryCode} {presenterPass.phone?.number}</p></div>
                    <div><p className="text-muted-foreground">Email</p><p className="font-medium">{presenterPass.email}</p></div>
                  </div>
                  <div className="text-xs text-muted-foreground">Claimed on {new Date(presenterPass.claimedAt).toLocaleString()}</div>
                </CardContent>
              </Card>
            )}
          </div>
        );
      } else {
        // Show "Reserved for Presenter" placeholder
        slots.push(
          <div key="presenter-reserved" className="border-2 border-dashed border-yellow-300 bg-yellow-50/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-yellow-700">
              <Trophy className="h-4 w-4" />
              <span className="font-medium">Reserved for Presenter</span>
            </div>
            <p className="text-xs text-yellow-600 mt-1">
              This gold pass is reserved for your company presenter. Submit presenter details to auto-claim this pass.
            </p>
          </div>
        );
      }
    }

    // Add other claimed passes (excluding presenter for gold, showing manual only for silver)
    const passesToShow = passType === 'gold' && passData.companyRole === 'company' ? nonPresenterPasses :
                         passType === 'silver' ? silverManualPasses :
                         passes;

    // Show FO nominee passes FIRST for Silver
    if (passType === 'silver' && silverFOPasses.length > 0) {
      silverFOPasses.forEach((pass, foIndex) => {
        const isExpanded = expandedPassIndex === `silver-fo-${foIndex}`;
        slots.push(
          <div key={`silver-fo-${foIndex}`} className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-between border-green-300 bg-green-50 hover:bg-green-100 hover:text-gray-900"
              onClick={() => {
                setExpandedPassIndex(isExpanded ? null : `silver-fo-${foIndex}`);
                setActivePassType(null);
              }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="font-medium">
                  {pass?.firstName} {pass?.lastName} - {pass?.designation}
                </span>
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full border border-green-300">
                  FO Nominee
                </span>
              </div>
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            {isExpanded && (
              <Card className="border-green-200">
                <CardContent className="pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-muted-foreground">First Name</p><p className="font-medium">{pass.firstName}</p></div>
                    <div><p className="text-muted-foreground">Last Name</p><p className="font-medium">{pass.lastName}</p></div>
                    <div><p className="text-muted-foreground">Designation</p><p className="font-medium">{pass.designation}</p></div>
                    <div><p className="text-muted-foreground">Company</p><p className="font-medium">{pass.company}</p></div>
                    <div><p className="text-muted-foreground">Phone</p><p className="font-medium">{pass.phone?.countryCode} {pass.phone?.number}</p></div>
                    <div><p className="text-muted-foreground">Email</p><p className="font-medium">{pass.email}</p></div>
                  </div>
                  <div className="text-xs text-muted-foreground">FO Nominee - Claimed on {new Date(pass.claimedAt).toLocaleString()}</div>
                  {pass.ticketCode && (
                    <Button
                      // style={{ display: 'none' }}
                      className="mt-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md"
                      onClick={() => window.open(`https://www.indiafintech.com/upgrade/?email=${pass.ticketCode}`, '_blank')}
                    >
                      Upgrade Pass
                      <ExternalLink className="ml-2 h-3 w-3" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        );
      });
    }

    // Then show manual claim passes
    for (let i = 0; i < Math.min(passesToShow.length, quota); i++) {
      const pass = passesToShow[i];
      if (!pass) continue;

      const isExpanded = expandedPassIndex === `${passType}-${i}`;

      slots.push(
        <div key={`${passType}-claimed-${i}`} className="space-y-2">
          <Button
            variant="outline"
            className="w-full justify-between border-green-300 bg-green-50 hover:bg-green-100 hover:text-gray-900"
            onClick={() => toggleClaimedPass(passType, i)}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="font-medium">
                {pass?.firstName} {pass?.lastName} - {pass?.designation}
              </span>
              {pass?.isFONominee && (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full border border-green-300">
                  FO Nominee
                </span>
              )}
            </div>
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>

          {isExpanded && pass && (
            <Card className="border-green-200">
              <CardContent className="pt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-muted-foreground">First Name</p><p className="font-medium">{pass.firstName}</p></div>
                  <div><p className="text-muted-foreground">Last Name</p><p className="font-medium">{pass.lastName}</p></div>
                  <div><p className="text-muted-foreground">Designation</p><p className="font-medium">{pass.designation}</p></div>
                  <div><p className="text-muted-foreground">Company</p><p className="font-medium">{pass.company}</p></div>
                  <div><p className="text-muted-foreground">Phone</p><p className="font-medium">{pass.phone?.countryCode} {pass.phone?.number}</p></div>
                  <div><p className="text-muted-foreground">Email</p><p className="font-medium">{pass.email}</p></div>
                </div>
                <div className="text-xs text-muted-foreground">Claimed on {new Date(pass.claimedAt).toLocaleString()}</div>
                {(passType === 'silverPlus' || passType === 'silver') && pass.ticketCode && (
                  <Button
                    // style={{ display: 'none' }}
                    className="mt-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md"
                    onClick={() => window.open(`https://www.indiafintech.com/upgrade/?email=${pass.ticketCode}`, '_blank')}
                  >
                    Upgrade Pass
                    <ExternalLink className="ml-2 h-3 w-3" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      );
    }

    // Calculate how many manual claim slots to show
    // For role='company' gold: exclude presenter
    // For silver: exclude FO bonus passes (only count manual claims)
    const manuallyClaimedCount = passType === 'gold' && passData.companyRole === 'company'
      ? nonPresenterPasses.length
      : passType === 'silver'
      ? silverManualPasses.length
      : passesToShow.length;

    const manualClaimQuota = passType === 'gold' && passData.companyRole === 'company' && quota > 0
      ? quota - 1  // Reserve 1 for presenter
      : quota;

    // Add available manual claim slots
    const available = manualClaimQuota - manuallyClaimedCount;
    if (available > 0 && activePassType !== passType) {
      slots.push(
        <Button
          key={`${passType}-claim`}
          variant="outline"
          className={`w-full justify-start border-${color}-300 bg-${color}-50 hover:bg-${color}-100 hover:text-gray-900`}
          onClick={() => handleClaimClick(passType)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Attendee
        </Button>
      );
    }

    // For Silver passes, ALWAYS show FO nomination button (additional to manual claims)
    if (passType === 'silver' && onOpenFOModal) {
      slots.push(
        <Button
          key="silver-fo"
          variant="outline"
          className="w-full justify-start border-green-300 bg-green-50 hover:bg-green-100 hover:text-gray-900"
          onClick={() => {
            onClose(); // Close passes modal
            onOpenFOModal(); // Open FO modal
          }}
        >
          <Trophy className="h-4 w-4 mr-2" />
          Nominate for Fintech Olympiad (2 Bonus Passes)
        </Button>
      );
    }

    // Show claim form if this pass type is active
    if (activePassType === passType) {
      slots.push(
        <div key={`${passType}-form`}>
          <PassAccordion
            passType={passType}
            companyName={passData.companyName}
            allPasses={[...passData.goldPasses, ...passData.silverPlusPasses, ...passData.silverPasses]}
            onClaim={handleClaim}
            onCancel={handleCancelClaim}
          />
        </div>
      );
    }

    return slots;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="w-full max-w-full sm:max-w-4xl max-h-[90vh] flex flex-col mx-2 sm:mx-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Users className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-base sm:text-lg">Get your IFTA passes For Your Team</span>
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Click "Add Attendee" and enter unique email and phone number for each attendee.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {fetchingData ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading pass information...</span>
            </div>
          ) : passData ? (
            <>
              {/* Lock Status Check */}
              {passData.isLocked ? (
                <div className="space-y-4">
                  {passData.lockReason === 'admin_locked' ? (
                    <Alert className="bg-amber-50 border-amber-200">
                      <Lock className="h-4 w-4 text-amber-600" />
                      <AlertDescription>
                        <div className="space-y-2">
                          <p className="font-medium text-amber-800">
                            Network pass allocation is coming soon!
                          </p>
                          <p className="text-sm text-amber-700">
                            {passData.lockMessage}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-amber-600 mt-2">
                            <Clock className="h-3 w-3" />
                            <span>Please check back later for updates.</span>
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : passData.lockReason === 'intake_not_completed' ? (
                    <Alert className="bg-blue-50 border-blue-200">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription>
                        <div className="space-y-2">
                          <p className="font-medium text-blue-800">
                            Complete Company Details First
                          </p>
                          <p className="text-sm text-blue-700">
                            {passData.lockMessage}
                          </p>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  {/* Show pass quota info even when locked */}
                  {/* <Card className="opacity-60">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Trophy className="h-4 w-4 text-yellow-600" />
                              <span className="text-sm font-medium">Gold Passes</span>
                            </div>
                            <span className="text-lg font-bold text-yellow-600">
                              {passData.goldQuota || 0}
                            </span>
                          </div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Ticket className="h-4 w-4 text-gray-600" />
                              <span className="text-sm font-medium">Silver Plus</span>
                            </div>
                            <span className="text-lg font-bold text-gray-600">
                              {passData.silverPlusQuota || 0}
                            </span>
                          </div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Ticket className="h-4 w-4 text-gray-600" />
                              <span className="text-sm font-medium">Silver Passes</span>
                            </div>
                            <span className="text-lg font-bold text-gray-600">
                              {passData.silverQuota || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-3">
                        You'll be able to allocate these passes to your team members once this feature is unlocked.
                      </p>
                    </CardContent>
                  </Card> */}
                </div>
              ) : (
                <>
                  {/* Progress Banner */}
                  {/* {(() => {
                    const totalQuota = passData.goldQuota + passData.silverQuota;
                    const effectiveClaimed = Math.min(passData.goldClaimed, passData.goldQuota) + Math.min(passData.silverClaimed, passData.silverQuota);

                    if (effectiveClaimed > 0) {
                      return (
                        <Alert className="bg-blue-50 border-blue-200">
                          <Info className="h-4 w-4 text-blue-600" />
                          <AlertDescription className="text-blue-800">
                            {effectiveClaimed} of {totalQuota} passes claimed. Keep going!
                          </AlertDescription>
                        </Alert>
                      );
                    }
                    return null;
                  })()} */}

                  {/* Pass Quota Summary */}
                  {/* <div className="grid grid-cols-3 gap-3">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm font-medium">Gold Passes</span>
                        </div>
                        <div className="text-right">
                          {passData.companyRole === 'company' && passData.goldQuota > 0 ? (
                            <>
                              <div className="text-lg font-bold text-yellow-600">
                                {Math.max(0, Math.min(passData.goldClaimed - 1, passData.goldQuota - 1))}/{passData.goldQuota - 1}
                              </div>
                              <div className="text-xs text-yellow-600">claimable</div>
                              <div className="text-[10px] text-black font-medium">+1 reserved</div>
                            </>
                          ) : (
                            <>
                              <div className="text-lg font-bold text-yellow-600">
                                {Math.min(passData.goldClaimed, passData.goldQuota)}/{passData.goldQuota || 0}
                              </div>
                              <div className="text-xs text-yellow-600">claimed</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Ticket className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium">Silver Plus</span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-600">
                            {Math.min(passData.silverPlusClaimed, passData.silverPlusQuota)}/{passData.silverPlusQuota || 0}
                          </div>
                          <div className="text-xs text-gray-600">claimed</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Ticket className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium">Silver Passes</span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-600">
                            {Math.min(passData.silverClaimed, passData.silverQuota)}/{passData.silverQuota || 0}
                          </div>
                          <div className="text-xs text-gray-600">claimed</div>
                          {passData.silverFOCount > 0 && (
                            <div className="text-[10px] text-green-600 font-medium">+{passData.silverFOCount} FO bonus</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div> */}

                  {/* Instructions */}
                  {/* <Alert className="text-sm">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Instructions:</strong> Click "Claim Pass" to allocate a pass to a team member. Each person must have unique email and phone number.
                    </AlertDescription>
                  </Alert> */}

                  {/* Gold Passes Section */}
                  {passData.goldQuota > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Trophy className="h-5 w-5 text-yellow-600" />
                        <h3 className="text-lg font-semibold">
                          Gold Passes ({Math.min(passData.goldClaimed, passData.goldQuota)}/{passData.goldQuota})
                        </h3>
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        {renderPassSlots('gold')}
                      </div>
                    </div>
                  )}

                  {/* Silver Plus Passes Section */}
                  {passData.silverPlusQuota > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Ticket className="h-5 w-5 text-gray-600" />
                        <h3 className="text-lg font-semibold">
                          Silver Plus Passes ({Math.min(passData.silverPlusClaimed, passData.silverPlusQuota)}/{passData.silverPlusQuota})
                        </h3>
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        {renderPassSlots('silverPlus')}
                      </div>
                    </div>
                  )}

                  {/* Silver Passes Section */}
                  {passData.silverQuota > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Ticket className="h-5 w-5 text-gray-600" />
                        <h3 className="text-lg font-semibold">
                          Silver Passes ({Math.min(passData.silverClaimed, passData.silverQuota)}/{passData.silverQuota})
                        </h3>
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        {renderPassSlots('silver')}
                      </div>
                    </div>
                  )}

                  {/* Benefits Note */}
                  <Alert className="text-xs bg-gray-50">
                    <Info className="h-3 w-3" />
                    <AlertDescription>
                      <a href="https://indiafintech.com/attend-ifta" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-semibold">Learn more about the pass benefits</a>
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </>
          ) : (
            <Alert variant="destructive">
              <AlertDescription>
                Unable to load pass information. Please try again.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Footer with Company Info */}
        {passData && !fetchingData && (
          <div className="flex-shrink-0 border-t pt-3">
            <div className="text-sm text-muted-foreground text-center">
              Company: <span className="font-medium">{passData.companyName}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
